import React from 'react';
import { Link } from 'react-router-dom';
import shared from './GarageContent.module.scss';
import card from '../DealerStockContent/DealerStockCard.module.scss';
import { formatDay } from '../../services/dealerGarage';
import placeholderImage from '../../media/images/emptyFavorites.png';

// The auction feed writes chassis codes in full-width characters ("ＪＺＳ１７１"),
// which render letter-spaced; NFKC folds them to plain "JZS171".
export const plain = (value) => String(value || '').normalize('NFKC').trim();

// "2001 TOYOTA CROWN 4D ATHLETE V": the auction feed keeps the year in `model`.
export const carTitle = (car) => {
    const detail = car.makerDetail || {};
    return [car.model, detail.maker, detail.model, detail.grade].map(plain).filter(Boolean).join(' ') || 'Auction car';
};

const carImage = (car) => (car.vehicleImages || [])[0]
    || (car.carDetail && (car.carDetail.vehicleDetailImages || [])[0])
    || car.image
    || placeholderImage;

/**
 * One auction car as a card, the same card as a saved dealer-stock car: the
 * photo with its starting bid and mileage, then the lot facts. My Garage's
 * Saved Cars and the admin inspection list both show cars this way.
 *
 * `linkProps` go on every link to the car page. `overlay` sits on the photo
 * but outside those links, so a click on it (the heart, a checkbox) does not
 * navigate. `children` follow the card body.
 */
const AuctionCarCard = ({ car, linkProps, overlay, className, children }) => {
    const bid = car.bidDetail || {};
    const title = carTitle(car);
    const chassis = plain((car.makerDetail || {}).type);
    const lot = plain(bid.bidNo || car.bidNo).replace(/^No\.?\s*/i, '');
    const toCar = { to: `/detail/${car._id}`, ...linkProps };

    return (
        <div className={[card.card, className].filter(Boolean).join(' ')}>
            <div className={card.media}>
                <Link {...toCar} className={card.imageLink}>
                    <div className={card.imageContainer}>
                        <img
                            src={carImage(car)}
                            alt={title}
                            loading="lazy"
                            onError={(e) => { e.target.onerror = null; e.target.src = placeholderImage; }}
                        />
                        {bid.start && <div className={card.priceBadge}>Start {plain(bid.start)}</div>}
                        {car.mileage && <div className={card.kmBadge}>● {plain(car.mileage)}</div>}
                    </div>
                </Link>
                {overlay}
            </div>

            <div className={card.cardBody}>
                <Link {...toCar} className={card.titleLink}>
                    <h3 className={card.title}>{title}</h3>
                </Link>

                <p className={shared.lotLine}>{[chassis, lot && `Lot ${lot}`].filter(Boolean).join(' · ')}</p>

                <div className={card.detailsGrid}>
                    <div className={card.detailItem}>
                        <i className="fas fa-star" aria-hidden="true"></i>
                        <span>Grade {plain(car.inspectionScore) || 'N/A'}</span>
                    </div>
                    <div className={card.detailItem}>
                        <i className="fas fa-gavel" aria-hidden="true"></i>
                        <span>{plain(bid.bidSite) || 'N/A'}</span>
                    </div>
                    <div className={card.detailItem}>
                        <i className="far fa-calendar" aria-hidden="true"></i>
                        <span>{bid.bidDate ? formatDay(bid.bidDate) : 'N/A'}</span>
                    </div>
                    <div className={card.detailItem}>
                        <i className="fas fa-cog" aria-hidden="true"></i>
                        <span>{plain((car.engineDetail || {}).transmission) || 'N/A'}</span>
                    </div>
                </div>

                <Link {...toCar} className={card.viewButton}>View Details</Link>
            </div>

            {children}
        </div>
    );
};

export default AuctionCarCard;
